/**
 * schedule — planned shifts, and whether reality matched the plan.
 *
 * The shift API has existed since Task 11 with no client and no Admin screen.
 * Its most valuable property for the admin experience is the one the week view
 * exposes: every shift carries the SESSION that fulfilled it, or null. That null
 * is what "who should be working but isn't" is made of, and it is why this
 * module returns the session alongside the shift rather than as a separate call.
 *
 * Reads need `schedule:read`; create/update need `schedule:write`; cancel,
 * delete and the association sweeps need `schedule:manage`.
 */
import { del, get, patch, post } from './transport.js';
/**
 * `GET /schedule/shifts` → `{ shifts, scope }`.
 *
 * THE FRAME THE SERVER STATES SURVIVES THE CLIENT. `routes.ts` narrows a caller
 * without `schedule:manage` to their own worker and says so in `scope`; this
 * package used to return `body.shifts` and drop it, which downstream is
 * indistinguishable from a server that never said. Prefer this over
 * {@link shifts} anywhere a count, a coverage figure or a safety sentence is
 * derived from the rows.
 */
export async function shiftRoster(query = {}, options) {
    return get('/schedule/shifts', { ...options, query: { ...query } });
}
/**
 * `GET /schedule/shifts?includeSession=true` → `{ shifts, scope }` — the same
 * roster and the same frame, plus whether each shift was fulfilled.
 *
 * This exists so "which shifts started with nobody clocked in" costs ONE
 * request. The admin coverage view used to answer it by fetching a week per
 * scheduled worker, which is a request per head and, measured on 120 workers,
 * 720 database queries for a question the roster answers in six.
 *
 * The coverage question is exactly the one that must not be asked without the
 * frame: "nothing went unrecorded" over an `own` list is a claim about one
 * person wearing the organization's clothes.
 */
export async function coverageRoster(query = {}, options) {
    return get('/schedule/shifts', {
        ...options,
        query: { ...query, includeSession: 'true' },
    });
}
/**
 * `GET /schedule/shifts` → the rows ALONE.
 *
 * DROPS `scope`, deliberately and by name, so that a caller choosing it has
 * chosen to be unable to say whose roster this is. Use {@link shiftRoster}
 * unless the rows are being rendered as themselves and nothing is being
 * counted, compared or declared safe.
 */
export async function shifts(query = {}, options) {
    return (await shiftRoster(query, options)).shifts;
}
/**
 * `GET /schedule/shifts?includeSession=true` → the rows ALONE. Drops `scope`;
 * see {@link shifts} and prefer {@link coverageRoster}.
 */
export async function shiftsWithCoverage(query = {}, options) {
    return (await coverageRoster(query, options)).shifts;
}
/** `GET /schedule/shifts/:id`. */
export async function shift(id, options) {
    const body = await get(`/schedule/shifts/${id}`, options);
    return body.shift;
}
/**
 * `GET /schedule/workers/:workerId/week` — the plan and what fulfilled it.
 *
 * `weekOf` is any instant inside the target week; the server anchors it.
 */
export async function workerWeek(workerId, weekOf, options) {
    const body = await get(`/schedule/workers/${workerId}/week`, {
        ...options,
        query: { weekOf },
    });
    return body.week;
}
/** `POST /schedule/shifts`. */
export async function createShift(input, options) {
    const body = await post('/schedule/shifts', input, options);
    return body.shift;
}
/** `PATCH /schedule/shifts/:id`. */
export async function updateShift(id, input, options) {
    const body = await patch(`/schedule/shifts/${id}`, input, options);
    return body.shift;
}
/**
 * `POST /schedule/shifts/:id/cancel` — cancel, keeping the record.
 *
 * Preferred over delete: a cancelled shift is history a coverage report needs,
 * whereas a deleted one silently improves the numbers.
 */
export async function cancelShift(id, options) {
    const body = await post(`/schedule/shifts/${id}/cancel`, undefined, options);
    return body.shift;
}
/** `DELETE /schedule/shifts/:id` — for a shift created in error. */
export async function deleteShift(id, options) {
    await del(`/schedule/shifts/${id}`, options);
}
/** `POST /schedule/associate` — link one session to one shift by hand. */
export async function associate(input, options) {
    return post('/schedule/associate', input, options);
}
/**
 * `POST /schedule/auto-match` — link every unassociated native session in the
 * window to the shift it best fulfils. Idempotent: an already-linked session is
 * left alone, so this is safe to call on every schedule read.
 *
 * With no `workerId` it sweeps the whole window in one transaction. The admin
 * coverage view used to call this once per scheduled worker on every render —
 * 2,160 queries across 120 connections, measured, and these are writes.
 */
export async function autoMatch(input, options) {
    return post('/schedule/auto-match', input, options);
}
// --------------------------------------------------------------- availability
/** `GET /availability/workers/:workerId` — `schedule:read` + a reach check. */
export async function workerAvailability(workerId, options) {
    const body = await get(`/availability/workers/${workerId}`, options);
    return body.availability;
}
/**
 * `POST /availability/workers/:workerId` → 201.
 *
 * TWO DOORS, and the narrow one is the point: `schedule:write` reaches
 * anybody, and `timeoff:request` reaches only your own worker record. A worker
 * must always be able to say when they can work.
 */
export async function addAvailability(workerId, input, options) {
    const body = await post(`/availability/workers/${workerId}`, input, options);
    return body.availability;
}
/**
 * `GET /availability/workers/:workerId/capacity` — `schedule:read` + reach.
 *
 * `anchor` is an INSTANT; the organization's own week is derived from it in
 * SQL. There is deliberately no way to hand in week bounds of your own, which
 * is the reliable way to measure a different week from the one the engine did.
 */
export async function workerCapacity(workerId, query = {}, options) {
    const body = await get(`/availability/workers/${workerId}/capacity`, {
        ...options,
        query: { ...query },
    });
    return body.capacity;
}
// -------------------------------------------------------------------- coverage
/** `GET /availability/coverage/recommendations` — needs `schedule:manage`. */
export async function coverageRecommendations(query, options) {
    const body = await get('/availability/coverage/recommendations', {
        ...options,
        query: { ...query },
    });
    return body.coverage;
}
/**
 * `POST /availability/coverage/offers` → 201. Needs `schedule:manage`.
 *
 * 409 when the audience is empty. That is not an error to swallow: it is the
 * server refusing to turn "nobody can cover this" into a broadcast.
 */
export async function createCoverageOffer(input, options) {
    const body = await post('/availability/coverage/offers', input, options);
    return body.offer;
}
/**
 * `POST /availability/coverage/offers/:id/accept` — needs `schedule:manage`.
 *
 * THE MANAGER'S DOOR, and it names the worker because that is the act: somebody
 * with roster authority deciding who takes the shift. A worker taking a shift
 * offered to THEM is {@link claimOpenShift}, which takes no worker id at all.
 *
 * 409 when the offer is closed, the candidacy already answered, or the shift no
 * longer assignable. Each of those is a different sentence and worth showing.
 */
export async function acceptCoverageOffer(offerId, workerId, options) {
    return post(`/availability/coverage/offers/${offerId}/accept`, { workerId }, options);
}
/**
 * `GET /availability/coverage/offers` — needs `schedule:manage`.
 *
 * The offers read back. Until this existed an offer was returned once, at
 * creation, and was unreadable for the rest of its life: a scheduler who closed
 * the drawer could not find out whether anybody had answered, and had no offer
 * id to accept against.
 *
 * `status` is not defaulted. A filled offer is the explanation for who is on the
 * shift now, and dropping it by default would render "nobody answered" and
 * "somebody took it" identically.
 */
export async function coverageOffers(filter = {}, options) {
    const body = await get('/availability/coverage/offers', {
        ...options,
        query: { ...filter },
    });
    return body.offers;
}
/**
 * `POST /availability/coverage/offers/:offerId/candidates/:workerId/include`
 * — needs `schedule:manage`.
 *
 * PUT ONE NAMED WORKER ON THE WORKER-ACTIONABLE SIDE OF AN OFFER. The founder's
 * third worker-reach ruling of 2026-09-09: `manager_led` stays load-bearing, and
 * a manager-led offer becomes browsable and claimable by a worker who is
 * EXPLICITLY INCLUDED. This is that inclusion, one person at a time — there is
 * no "release the shortlist", because releasing a shortlist wholesale is an
 * `open_shift` offer by another name.
 *
 * Idempotent: a second call answers 200 with `alreadyIncluded: true`, changes
 * nothing, and does not notify the worker again.
 *
 * 409 when the offer is closed or the candidacy already answered; 404-shaped
 * refusals do not arise, because this route is behind the org-wide roster key
 * and its caller already reads the whole offer list.
 */
export async function includeCandidateInWorkerOffers(offerId, workerId, options) {
    return post(`/availability/coverage/offers/${offerId}/candidates/${workerId}/include`, undefined, options);
}
/**
 * `GET /availability/coverage/open-shifts` — `coverage:read_own`, self only.
 *
 * Shifts offered to the CALLER and still claimable: the candidacy has been made
 * worker-actionable, offer open,
 * candidacy unanswered, not past its expiry, and the shift still assignable. An
 * empty array is a real measured zero — nothing is open to you — and not a
 * refusal; a caller with no worker row gets 403 instead, so the two cannot be
 * confused.
 */
export async function myOpenShifts(options) {
    const body = await get('/availability/coverage/open-shifts', options);
    return body.openShifts;
}
/**
 * `POST /availability/coverage/open-shifts/:offerId/claim` — `coverage:claim_own`,
 * self only.
 *
 * 403 means this shift is not yours to claim — it names nothing, it was never
 * offered to you, or it is a manager's shortlist you have not been included on.
 * One sentence for every such case on purpose, so the refusals cannot be walked
 * as an oracle. A screen should say it and not offer a retry.
 *
 * 409 means the world moved: somebody took it, you already answered, the shift
 * was cancelled. Different sentence, different action — reload, do not retry.
 */
export async function claimOpenShift(offerId, options) {
    return post(`/availability/coverage/open-shifts/${offerId}/claim`, undefined, options);
}
/**
 * `POST /availability/coverage/open-shifts/:offerId/decline` —
 * `coverage:claim_own`, self only.
 *
 * Answers ONE row, the caller's own candidacy, and never the offer: the shift
 * stays open and the other candidates keep theirs. It is accepted even when the
 * offer has already been filled by somebody else, because a candidacy left at
 * "offered" forever reads as an unanswered ask rather than as a question that
 * stopped mattering.
 */
export async function declineOpenShift(offerId, options) {
    return post(`/availability/coverage/open-shifts/${offerId}/decline`, undefined, options);
}
// ------------------------------------------------------------------- ceilings
/**
 * `GET /schedule/clients/:clientId/service-level` — needs `service:manage`.
 *
 * NOT `service:read`, since PH-AUTHZ-003. The route had no per-client narrowing
 * of any kind — possession of a uuid was the whole gate — and `service:read` is
 * seeded to the stock `worker` role, so every field worker could read any
 * client's payer and payer reference. Reading a ceiling is a management act.
 *
 * Three quantities side by side with the disagreements named, and
 * deliberately no endpoint returning one number for "this client's hours".
 */
export async function clientServiceLevel(clientId, query = {}, options) {
    return get(`/schedule/clients/${clientId}/service-level`, { ...options, query: { ...query } });
}
// ===========================================================================
// CANCELLATION RECOVERY, AND THE CEILING'S WRITE SIDE
// ===========================================================================
//
// The section above says these bindings "come back with the surfaces that call
// them". They have. Each one below is called by a screen in the same commit
// that added it, which is the condition `client-binding-callers.test.ts` sets.
//
// ---------------------------------------------------------------------------
// TWO CANCEL DOORS EXIST AND THEY ARE NOT THE SAME ACT
// ---------------------------------------------------------------------------
// `cancelShift` above is `POST /schedule/shifts/:id/cancel`: it sets the shift's
// status and records NOTHING about the cancellation. `cancelShiftWithAttribution`
// is `POST /schedule/shifts/:id/cancellation`, which additionally writes a
// `shift_cancellation` row, opens the reschedule obligation, and — for a
// worker-attributed cancellation with no reason yet — puts the single ask to the
// staff member. Five states the plain door cannot produce.
//
// The plain door is NOT removed here. It is still the honest call for a shift
// being unwound rather than cancelled (a duplicate, a booking error), where
// there is no absence to explain and nobody to ask. What was wrong was that it
// was the only door a screen could open.
//
// ---------------------------------------------------------------------------
// THE TWO VOCABULARIES ARE RUNTIME CONSTANTS, NOT TYPES
// ---------------------------------------------------------------------------
// A picker needs the list at RUNTIME, and a union type is erased. Retyping the
// members into a screen is how a client offers a category the server's CHECK
// constraint refuses — so they are declared once, here, and
// `apps/admin/test/unit/cancellation-vocabulary.test.ts` reads the server's own
// zod schemas out of `apps/api/src/domains/availability/schemas.ts` and asserts
// these are exactly equal to them. Adding a member on either side without the
// other fails that test by name.
/** Whose side a cancellation came from. NOT which account performed it. */
export const CANCELLATION_ATTRIBUTIONS = [
    'worker',
    'client',
    'organization',
    'external',
    'unknown',
];
/** The closed list of reasons. Mirrors `shift_cancellation_reason_ck`. */
export const CANCELLATION_REASON_CATEGORIES = [
    'illness',
    'family',
    'transport',
    'weather',
    'scheduling_conflict',
    'client_unavailable',
    'client_declined',
    'safety',
    'other',
];
/**
 * The FIVE positions of the conversation, computed by the server.
 *
 * Never derived on the client. `overdue` and `escalated` are comparisons against
 * `now()`, and the server makes them in the same statement — and the same clock
 * — that `escalateUnanswered` sweeps with. A handset or a browser tab deciding
 * for itself whether a deadline has passed is a second opinion about what time
 * it is, and the two disagree under exactly the load that makes the queue
 * matter.
 */
export const CANCELLATION_ASK_STATES = [
    'not_asked',
    'awaiting',
    'overdue',
    'escalated',
    'answered',
];
/**
 * `POST /schedule/shifts/:id/cancellation` → 201. Needs `schedule:manage`.
 *
 * 409 when the shift is already cancelled, and that refusal is worth showing
 * verbatim: a second cancellation would be two conflicting stories about one
 * absence, which is what `shift_cancellation_org_shift_uk` refuses.
 */
export async function cancelShiftWithAttribution(shiftId, input, options) {
    return post(`/schedule/shifts/${shiftId}/cancellation`, input, options);
}
/**
 * `GET /schedule/shifts/:id/cancellation` — `schedule:read` plus a reach check.
 *
 * `null` means THIS SHIFT WAS NEVER CANCELLED, which is the ordinary case and
 * not a failed read. The route answers 200 with a null body rather than 404 for
 * exactly that reason; a 404 here would make the ordinary case look broken.
 */
export async function shiftCancellation(shiftId, options) {
    const body = await get(`/schedule/shifts/${shiftId}/cancellation`, options);
    return body.cancellation;
}
/**
 * `GET /schedule/cancellations` — needs `schedule:manage`, the ORG-WIDE roster
 * key. This is a management view of the organization's outstanding questions and
 * is deliberately not reachable from a worker surface.
 *
 * `states` is sent as a REPEATED query parameter, which the shared
 * `QueryParams` type cannot express (its values are scalars), so the string is
 * built here rather than by widening the transport for one caller.
 */
export async function cancellations(filter = {}, options) {
    const params = new URLSearchParams();
    for (const state of filter.states ?? [])
        params.append('state', state);
    if (filter.workerId !== undefined)
        params.append('workerId', filter.workerId);
    if (filter.limit !== undefined)
        params.append('limit', String(filter.limit));
    const qs = params.toString();
    const body = await get(`/schedule/cancellations${qs === '' ? '' : `?${qs}`}`, options);
    return body.cancellations;
}
/**
 * `GET /schedule/cancellations/:id` — `schedule:read` plus a reach check.
 *
 * THE REACH CHECK IS WHAT MAKES THIS SAFE TO CALL FROM A HANDSET. Until it
 * existed, `schedule:read` alone answered for any cancellation id in the tenant,
 * and the seeded `worker` role holds `schedule:read` — so a field worker could
 * read any colleague's `reasonNote`, which is free text a person wrote about why
 * they could not come to work. A 403 from here is "not yours", and a caller
 * should say that rather than offering a retry.
 */
export async function cancellation(id, options) {
    const body = await get(`/schedule/cancellations/${id}`, options);
    return body.cancellation;
}
/**
 * `POST /schedule/cancellations/:id/reason` — `timeoff:request` plus the reach
 * check, so the worker whose shift it was may answer, and so may a roster
 * holder on their behalf.
 *
 * 409 when a reason is already recorded. A reason is given ONCE; the refusal is
 * the product saying it will not overwrite somebody's account of their own day.
 */
export async function answerCancellation(cancellationId, input, options) {
    const body = await post(`/schedule/cancellations/${cancellationId}/reason`, input, options);
    return body.cancellation;
}
/**
 * `POST /schedule/cancellations/escalate-unanswered` — needs `schedule:manage`.
 *
 * A SWEEP, and therefore a button and never a render-time call. It moves every
 * asked-unanswered-past-deadline row to the manager the org chart names. The
 * returned `escalatedToUserId` is `null` where the org chart names nobody, which
 * is a real answer about the org chart and not a failure.
 */
export async function escalateUnansweredCancellations(options) {
    const body = await post('/schedule/cancellations/escalate-unanswered', undefined, options);
    return body.escalated;
}
/**
 * `GET /schedule/authorizations` — needs `service:manage`, and additionally
 * `directory:read_all` when `clientId` is omitted.
 *
 * The bare call is not a smaller question than the filtered one, it is the
 * LARGEST: `readAuthorizations` filters on `($1::uuid is null or client_id =
 * $1)`, so omitting the id returns every ceiling in the organization — payer
 * and payer reference for every client the agency serves. PH-AUTHZ-003 put that
 * shape behind `directory:read_all`, the same key `GET /directory/assignments`
 * charges for the same shape of question, and moved the read itself off
 * `service:read` (which is seeded to the stock `worker` role).
 *
 * With no `status` filter this returns REVOKED and EXPIRED ceilings alongside
 * active ones, on purpose. A revoked authorization is the explanation for work
 * that already happened under it; a list that dropped it would render "revoked"
 * and "never authorized" identically, which are opposite answers to a payer's
 * question.
 */
export async function authorizations(filter = {}, options) {
    const body = await get('/schedule/authorizations', {
        ...options,
        query: { ...filter },
    });
    return body.authorizations;
}
/**
 * `POST /schedule/authorizations` → 201. Needs `service:manage`, NOT a
 * scheduling key: putting the ceiling behind `schedule:*` would say that whoever
 * runs the roster may decide what a payer authorised.
 */
export async function createAuthorization(input, options) {
    const body = await post('/schedule/authorizations', input, options);
    return body.authorization;
}
/**
 * `POST /schedule/authorizations/:id/revoke` — needs `service:manage`.
 *
 * REVOKE, NEVER DELETE. `cwos_app` holds no DELETE on `service_authorization`,
 * so this is the only way a ceiling stops applying, and the row survives to
 * answer "was this visit authorised at the time". A revoked ceiling is not an
 * absent one and must never be rendered as one.
 */
export async function revokeAuthorization(id, input = {}, options) {
    const body = await post(`/schedule/authorizations/${id}/revoke`, input, options);
    return body.authorization;
}
//# sourceMappingURL=schedule.js.map